import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tldraw } from 'tldraw';
import 'tldraw/tldraw.css';
import { Box, Typography } from '@mui/material';
import LoadingScreen from 'components/loadingScreen';
import { useSelector } from 'react-redux';

import { useBoardStore } from '../store/useBoardStore';
import { readCurrentStep } from '../utils/boardGoalFields';
import { BoardActivitiesProvider } from '../context/BoardActivitiesContext';
import { BoardAiProvider } from '../context/BoardAiContext';
import { BOARD_SHAPE_UTILS } from '../shapes/boardShapeUtils';
import {
  getActivityId,
} from '../utils/autoLayout';
import { canvasDocumentHydrating } from '../utils/canvasHydration';
import {
  extractBoardDocumentSnapshot,
  hydrateBoardDocument,
  ensureFramesFromBoardMeta,
  framesFromEditor,
  mergePlacementsFromShapes,
  shouldPersistPlacements,
} from '../utils/boardSnapshot';
import {
  isPinCardDocumentChange,
  isPinCardHeightOnlyUpdate,
  isPinCardIndexOnlyUpdate,
  hasFrameDocumentChange,
  isFramePositionChange,
  isLabelOnlyDocumentChange,
} from '../utils/boardDocumentChanges';
import { buildBoardDocumentOps } from '../utils/buildBoardDocumentOps';
import { isCanvasStrokeInProgress } from '../utils/canvasToolState';
import { registerPinCardDragGuard } from '../utils/pinCardDrag';
import {
  deleteUserPins,
  registerUserPinDeleteCascade,
} from '../utils/deleteUserPins';
import {
  deleteSelectedCanvasShapes,
  registerEraserCanvasDelete,
} from '../utils/deleteCanvasPins';
import {
  classifyClipboardPayload,
  isTldrawClipboardEvent,
  payloadFromPasteEvent,
  promptAndPasteClipboard,
} from '../utils/canvasEmptySpaceActions';
import { isPendingUrlActivityId } from '../utils/pendingUrlPin';
import { registerFramePinFollow } from '../utils/framePinFollow';
import { registerFrameArrowTargeting } from '../utils/frameArrowTargeting';
import { registerBoardLinkDefaults, normalizeExistingBoardLinks } from '../utils/arrowTerminalLabels';
import { registerArrowSelectConnected } from '../utils/arrowSelectConnected';
import { registerCanvasShapeContextMenu } from '../utils/openTldrawContextMenu';
import {
  LABEL_PERSIST_MS,
  registerBoardAnnotationPersist,
} from '../utils/registerBoardAnnotationPersist';
import {
  clearNewlyAddedShapes,
  registerNewlyAddedShapeHighlight,
} from '../utils/newlyAddedShapes';
import { syncPinShapes } from '../utils/syncPinShapes';
import PinFocusPanel from './PinFocusPanel';
import YoutubeVideoModal from './YoutubeVideoModal';
import { membershipIdFromFrameShape } from '../utils/frameMembership';
import {
  CANVAS_CAMERA_ZOOM_STEPS,
  clampCardViewZoom,
} from 'constants/cardImageZoom';
import {
  patchBoardViewportKeepalive,
  patchBoardPlacementOpsKeepalive,
} from '../api/boardApi';
import { buildPlacementOps } from '../utils/placementOps';
import {
  registerPlacementPersister,
  unregisterPlacementPersister,
} from '../utils/boardPlacementPersist';
import {
  quantizeViewport,
  resolveBoardViewport,
  writeLocalBoardViewport,
} from '../utils/boardViewportPersist';
import { syncBoardCameraCssVars } from '../utils/boardCameraCss';
import BoardAiToolbar from './BoardAiToolbar';
import BoardCanvasToolbar from './BoardCanvasToolbar';
import BoardAlertHost from './BoardAlertHost';
import BoardNavigationPanel from './BoardNavigationPanel';
import BoardSharePanel from './BoardSharePanel';
import BoardStylePanel from './BoardStylePanel';
import PinnedChatViewer from './PinnedChatViewer';
import BoardChatLauncher from './BoardChatLauncher';
import BoardContextMenu from './BoardContextMenu';
import BoardEmptyOnboarding from './BoardEmptyOnboarding';
import BoardCurrentStep from './BoardCurrentStep';
import BoardSettledPanel from './BoardSettledPanel';
import CanvasFrontOverlays from './CanvasFrontOverlays';
import { useBoardCollabSync } from '../hooks/useBoardCollabSync';
import { useBoardSteps } from '../hooks/useBoardSteps';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { useCanvasBoardSocket } from '../hooks/useCanvasBoardSocket';
import { createOpsOnlyDocumentSaver } from '../utils/persistBoardDocumentOps';

import './boardCanvas.css';

const CANVAS_BOARD_RELOAD_MIN_INTERVAL_MS = 800;
const VIEWPORT_SAVE_DEBOUNCE_MS = 1200;
const OPS_SAVE_DEBOUNCE_MS = 180;
const FRAME_SAVE_DEBOUNCE_MS = 220;
function buildPinSyncKey(activities, board) {
  const activityKey = (activities || []).map(a => getActivityId(a)).join(',');
  const placementsKey = (board?.placements || [])
    .map(p => `${p.activityId}:${p.x}:${p.y}:${p.frameId ?? ''}`)
    .join('|');
  return `${activityKey}::${placementsKey}`;
}

function shapesToPlacements(editor, existingPlacements = [], activities = []) {
  const byId = new Map(
    (existingPlacements || []).map(p => [String(p.activityId), p])
  );
  const fromShapes = editor
    .getCurrentPageShapes()
    .filter(s => s.type === 'pin-card' && s.meta?.activityId)
    .map(s => {
      const bounds = editor.getShapePageBounds(s);
      const parent = s.parentId ? editor.getShape(s.parentId) : null;
      const frameIdFromParent =
        parent?.type === 'frame' ? membershipIdFromFrameShape(parent) : null;
      const prev = byId.get(String(s.meta.activityId));
      const prevSource = String(prev?.placementSource || '');
      // Manual drag/save settles inbox drafts as user-placed.
      const placementSource =
        prevSource === 'ai' || prevSource === 'preferred' || prevSource === 'user'
          ? prevSource
          : 'user';
      return {
        activityId: s.meta.activityId,
        x: bounds?.x ?? s.x,
        y: bounds?.y ?? s.y,
        width: bounds?.w ?? s.props.w,
        height: bounds?.h ?? s.props.h,
        zIndex: 0,
        manualSize: Boolean(s.meta?.manualHeight),
        frameId: frameIdFromParent || s.meta?.frameId || null,
        placementSource,
        ...(prev?.pinCase ? { pinCase: prev.pinCase } : {}),
      };
    });
  // Keep settled positions for pins not yet on the page (partial sync), but
  // drop placements whose activities were deleted.
  const merged = mergePlacementsFromShapes(fromShapes, existingPlacements);
  if (!activities?.length) return merged;
  const validIds = new Set(activities.map(a => getActivityId(a)).filter(Boolean));
  return merged.filter(p => validIds.has(String(p.activityId)));
}

function hasPersistedCanvasContent(board) {
  return Boolean(
    board?.tldrawSnapshot?.store && Object.keys(board.tldrawSnapshot.store).length > 0
  );
}

function decodeRouteParam(value) {
  if (!value) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function applyEditorReadonly(editor, canEdit) {
  editor.updateInstanceState({ isReadonly: !canEdit });
}

function applyBoardViewport(editor, viewport) {
  if (!editor || !viewport) return;
  editor.setCamera(
    {
      x: viewport.x ?? 0,
      y: viewport.y ?? 0,
      z: clampCardViewZoom(viewport.zoom ?? 1),
    },
    { force: true }
  );
}

function cameraToViewport(editor) {
  const cam = editor.getCamera();
  return quantizeViewport({ x: cam.x, y: cam.y, zoom: cam.z });
}

// These tools are not on the board toolbar. Drop their shortcuts too.
const HIDDEN_TOOLBAR_TOOL_IDS = ['hand', 'eraser', 'rectangle', 'note', 'draw', 'asset'];

function withoutToolbarShortcuts(tools) {
  const next = { ...tools };
  for (const id of HIDDEN_TOOLBAR_TOOL_IDS) {
    const tool = next[id];
    if (!tool || !tool.kbd) continue;
    next[id] = { ...tool, kbd: undefined };
  }
  return next;
}

function resolvePastePagePoint(editor) {
  const point = editor?.inputs?.currentPagePoint;
  if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) {
    return { x: point.x, y: point.y };
  }
  return { x: 80, y: 80 };
}

const TldrawBoard = ({ personName, hashtag, loadedTime }) => {
  const user = useSelector(state => state.user);
  const { extensionId } = useSelector(state => state.api);
  const decodedPersonName = decodeRouteParam(personName);
  const decodedHashtag = decodeRouteParam(hashtag);

  const {
    loadBoard,
    loading,
    error,
    errorCode,
    activities,
    board,
    isOwner,
    canEdit: storeCanEdit,
    role,
    personName: storedPersonName,
    hashtag: storedHashtag,
    updatePlacementsLocal,
    savePlacements,
    saveFrames,
    saveViewport,
    saveDocumentOps,
    updateSnapshotLocal,
    prependActivity,
    deleteActivities,
    selectedActivityId,
    setSelectedActivityId,
    youtubePlayback,
    closeYoutubePlayback,
    saveYoutubeMoment,
  } = useBoardStore();

  const editorRef = useRef(null);
  const openConversationRef = useRef(null);
  const openBoardChatRef = useRef(null);
  const startChatWithMessageRef = useRef(null);
  const analyzePinAddedRef = useRef(null);
  const enqueueDigestDeleteRef = useRef(null);
  const hydratedBoardKeyRef = useRef(null);
  const syncedActivityIdsRef = useRef('');
  const userActorRef = useRef(null);
  const [pinnedViewer, setPinnedViewer] = useState({ open: false, conversationId: null });
  const prevBoardKeyRef = useRef(null);
  const isHydratingRef = useRef(false);
  const annotationMigrationPersistRef = useRef(null);
  const skipNewlyAddedHighlightRef = useRef(false);
  const lastBoardReloadAtRef = useRef(0);
  const pendingPinActivityIdRef = useRef(null);
  const refreshContextActionsRef = useRef(null);
  const { geminiLanguage } = useGeminiLanguage();

  userActorRef.current = user
    ? {
        id: String(user._id),
        username: user.username || '',
        profileImageUrl: user.profileImageUrl || '',
      }
    : null;

  const canEdit = useMemo(() => {
    if (storeCanEdit) return true;
    if (!isOwner || !user?.username || !decodedPersonName) return false;
    return user.username.toLowerCase() === decodedPersonName.toLowerCase();
  }, [storeCanEdit, isOwner, user?.username, decodedPersonName]);

  const handleRemoteCollabSnapshot = useCallback(snapshot => {
    const editor = editorRef.current;
    if (!editor || !snapshot) return;
    try {
      const { activities: acts, board: brd } = dataRef.current;
      hydrateBoardDocument(editor, snapshot, { activities: acts, board: brd });
      updateSnapshotLocal(snapshot);
      if (brd) syncPinShapes(editor, acts, brd);
    } catch (err) {
      console.warn('Failed to apply remote collab snapshot', err);
    }
  }, [updateSnapshotLocal]);

  useBoardCollabSync({
    enabled: Boolean(board?._id && (board?.collaborators || []).some(c => c.status === 'active')),
    boardId: board?._id,
    editorRef,
    canEdit,
    onRemoteSnapshot: handleRemoteCollabSnapshot,
  });

  // Viewers: open pinned conversation pins in read-only shared viewer
  useEffect(() => {
    if (canEdit) return undefined;
    openConversationRef.current = conversationId => {
      if (!conversationId) return;
      setPinnedViewer({ open: true, conversationId: String(conversationId) });
    };
    return () => {
      openConversationRef.current = null;
    };
  }, [canEdit]);

  const handleSocketBoardUpdated = useCallback(
    payload => {
      const isPinEvent = (
        (payload?.reason === 'pin_added' || payload?.reason === 'pin_placed')
        && payload?.activity
      );
      if (isPinEvent) {
        const pinId = String(
          payload.activity._id || payload.activity.actionId || ''
        );
        pendingPinActivityIdRef.current = pinId;
        // Classify immediately — do not wait for shape sync, and do not let
        // the board-reload throttle drop pin-case for extension adds.
        if (pinId) {
          analyzePinAddedRef.current?.({
            activityId: pinId,
            placePending: true,
          });
          pendingPinActivityIdRef.current = null;
        }

        // Swap optimistic URL-paste pin → real activity, keeping the local position
        // (including any drag while loading).
        const store = useBoardStore.getState();
        const realId = getActivityId(payload.activity);
        const incomingUrl = String(
          payload.activity?.pendingUrl
            || payload.activity?.content?.[0]?.urls?.[0]?.href
            || payload.activity?.content?.[0]?.url
            || ''
        ).trim();
        const pending = (store.activities || []).find(activity => {
          if (!activity?.pendingUrlLoad && !isPendingUrlActivityId(getActivityId(activity))) {
            return false;
          }
          if (!incomingUrl) return true;
          const pendingUrl = String(
            activity.pendingUrl || activity.content?.[0]?.urls?.[0]?.href || ''
          ).trim();
          return !pendingUrl || pendingUrl === incomingUrl;
        });
        if (pending && realId) {
          const pendingId = getActivityId(pending);
          const pendingPlacement = (store.board?.placements || []).find(
            p => String(p.activityId) === pendingId
          );
          store.removeActivitiesLocal([pendingId]);
          if (pendingPlacement) {
            const placements = (useBoardStore.getState().board?.placements || []).filter(
              p => String(p.activityId) !== realId
            );
            useBoardStore.getState().updatePlacementsLocal([
              ...placements,
              {
                ...pendingPlacement,
                activityId: realId,
                placementSource: 'preferred',
              },
            ]);
          }
        }
      }

      if (payload?.activity) {
        prependActivity(payload.activity);
      }

      const now = Date.now();
      if (now - lastBoardReloadAtRef.current < CANVAS_BOARD_RELOAD_MIN_INTERVAL_MS) return;
      lastBoardReloadAtRef.current = now;

      // Force re-sync when board placements arrive after optimistic prepend.
      syncedActivityIdsRef.current = '';
      loadBoard(decodedPersonName, decodedHashtag, { force: true });
    },
    [decodedPersonName, decodedHashtag, loadBoard, prependActivity]
  );

  useCanvasBoardSocket({
    enabled: Boolean(decodedPersonName && decodedHashtag),
    personName: decodedPersonName,
    hashtag: decodedHashtag,
    onBoardUpdated: handleSocketBoardUpdated,
  });

  const persistDocumentOpsOnly = useCallback(
    snapshot => createOpsOnlyDocumentSaver(editorRef.current)(snapshot),
    [editorRef]
  );

  const boardAiContextValue = useMemo(
    () => ({
      hashtag: decodedHashtag,
      extensionId,
      // Ops-only — never PATCH full /snapshot from pin dismiss / AI helpers.
      onSaveSnapshot: persistDocumentOpsOnly,
      openConversation: (conversationId, options) => {
        openConversationRef.current?.(conversationId, options);
      },
      registerOpenConversation: handler => {
        openConversationRef.current = handler;
      },
      openBoardChat: () => {
        openBoardChatRef.current?.();
      },
      registerOpenBoardChat: handler => {
        openBoardChatRef.current = handler;
      },
      startChatWithMessage: (message, options) => {
        startChatWithMessageRef.current?.(message, options);
      },
      registerStartChatWithMessage: handler => {
        startChatWithMessageRef.current = handler;
      },
      analyzePinAdded: (...args) => analyzePinAddedRef.current?.(...args),
      enqueueDigestDelete: (...args) => enqueueDigestDeleteRef.current?.(...args),
    }),
    [decodedHashtag, extensionId, persistDocumentOpsOnly]
  );

  const pinCount = activities.length;
  const dataRef = useRef({ activities, board, canEdit });
  const boardKey = `${decodedPersonName}:${decodedHashtag}`;
  const [canvasHydrationTick, setCanvasHydrationTick] = useState(0);
  const canvasHydrationTimerRef = useRef(null);
  const canvasHydrationAttemptRef = useRef(0);
  const scheduleCanvasContextAnalysis = useCallback(() => {
    clearTimeout(canvasHydrationTimerRef.current);
    const attemptId = canvasHydrationAttemptRef.current + 1;
    canvasHydrationAttemptRef.current = attemptId;

    const tryMarkReady = (attempt = 0) => {
      if (canvasHydrationAttemptRef.current !== attemptId) return;

      const editor = editorRef.current;
      const { activities: acts } = dataRef.current;

      if (!editor) return;

      if (isHydratingRef.current) {
        if (attempt < 30) {
          canvasHydrationTimerRef.current = setTimeout(() => tryMarkReady(attempt + 1), 50);
        }
        return;
      }

      const expectedPinCount = acts.length;
      const actualPinCount = editor
        .getCurrentPageShapes()
        .filter(shape => shape.type === 'pin-card').length;

      if (expectedPinCount > 0 && actualPinCount < expectedPinCount) {
        if (attempt < 40) {
          canvasHydrationTimerRef.current = setTimeout(() => tryMarkReady(attempt + 1), 50);
        }
        return;
      }

      requestAnimationFrame(() => {
        if (canvasHydrationAttemptRef.current !== attemptId) return;
        requestAnimationFrame(() => {
          if (canvasHydrationAttemptRef.current !== attemptId || isHydratingRef.current) return;
          setCanvasHydrationTick(tick => tick + 1);
        });
      });
    };

    canvasHydrationTimerRef.current = setTimeout(() => tryMarkReady(0), 0);
  }, []);
  const {
    contextActions,
    contextLoading,
    contextError,
    thinkingPhase,
    lastJobMetrics,
    refreshContextActions,
    analyzePinAdded,
    enqueueDigestDelete,
    dismissNextAction,
    recordGapInterest,
    recordUserDecision,
  } = useBoardSteps({
    editorRef,
    hashtag: decodedHashtag,
    board,
    pinCount,
    canvasHydrationTick,
    locale: geminiLanguage,
  });
  analyzePinAddedRef.current = analyzePinAdded;
  enqueueDigestDeleteRef.current = enqueueDigestDelete;

  useEffect(() => {
    refreshContextActionsRef.current = refreshContextActions;
  }, [refreshContextActions]);

  const tldrawComponents = useMemo(
    () => ({
      MenuPanel: null,
      Toolbar: BoardCanvasToolbar,
      ContextMenu: BoardContextMenu,
      InFrontOfTheCanvas: CanvasFrontOverlays,
      NavigationPanel: BoardNavigationPanel,
      StylePanel: BoardStylePanel,
    }),
    []
  );
  const tldrawOverrides = useMemo(
    () => ({
      tools: (_editor, tools) => withoutToolbarShortcuts(tools),
      actions: (editor, actions) => ({
        ...actions,
        delete: {
          ...actions.delete,
          onSelect: () => {
            if (!dataRef.current.canEdit || isHydratingRef.current) return;
            deleteSelectedCanvasShapes(editor, { confirmUserPins: true }).catch(err => {
              console.warn('Failed to delete selected canvas shapes', err);
            });
          },
        },
        'insert-media': actions['insert-media']
          ? { ...actions['insert-media'], kbd: undefined }
          : actions['insert-media'],
      }),
    }),
    []
  );
  const isBoardDataReady =
    !loading &&
    decodedPersonName === storedPersonName &&
    decodedHashtag === storedHashtag &&
    board != null;

  dataRef.current = { activities, board, canEdit };

  useEffect(() => {
    if (decodedPersonName && decodedHashtag) {
      hydratedBoardKeyRef.current = null;
      syncedActivityIdsRef.current = '';
      loadBoard(decodedPersonName, decodedHashtag, { force: true });
    }
  }, [decodedPersonName, decodedHashtag, loadBoard]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !isBoardDataReady) return;
    applyEditorReadonly(editor, canEdit);
  }, [canEdit, isBoardDataReady]);

  useEffect(() => {
    if (prevBoardKeyRef.current === boardKey) return;

    if (prevBoardKeyRef.current !== null) {
      hydratedBoardKeyRef.current = null;
      syncedActivityIdsRef.current = '';
      editorRef.current?._boardCleanup?.();
      editorRef.current = null;
      clearTimeout(canvasHydrationTimerRef.current);
      canvasHydrationAttemptRef.current += 1;
      setCanvasHydrationTick(0);
    }

    prevBoardKeyRef.current = boardKey;
  }, [boardKey]);

  const hydrateEditor = useCallback((editor, acts, brd, onHydratedComplete) => {
    isHydratingRef.current = true;
    canvasDocumentHydrating.active = true;
    skipNewlyAddedHighlightRef.current = true;
    const preferLocal = dataRef.current.canEdit;
    const viewport = resolveBoardViewport(brd, decodedPersonName, decodedHashtag, {
      preferLocal,
    });
    try {
      editor.run(() => {
        if (brd.placements?.length || hasPersistedCanvasContent(brd)) {
          // Pass activities/board so pin stubs are seeded before bindings load —
          // otherwise connect lines bound to pins lose their endpoints on refresh.
          hydrateBoardDocument(editor, brd?.tldrawSnapshot, {
            activities: acts,
            board: brd,
          });
          // Restore frames from board.frames when the snapshot omitted them.
          ensureFramesFromBoardMeta(editor, brd?.frames);
          syncPinShapes(editor, acts, brd);
        }
      });
      applyBoardViewport(editor, viewport);
      if (viewport && preferLocal) {
        writeLocalBoardViewport(decodedPersonName, decodedHashtag, viewport);
      }
      // Re-apply after layout; keep isHydrating until then so early camera
      // events from setCamera don't overwrite the restored view.
      requestAnimationFrame(() => {
        if (editorRef.current !== editor) {
          isHydratingRef.current = false;
          canvasDocumentHydrating.active = false;
          skipNewlyAddedHighlightRef.current = false;
          return;
        }
        applyBoardViewport(editor, viewport);
        requestAnimationFrame(() => {
          if (editorRef.current === editor) {
            applyBoardViewport(editor, viewport);
            clearNewlyAddedShapes(editor.getContainer());
          }
          isHydratingRef.current = false;
          canvasDocumentHydrating.active = false;
          skipNewlyAddedHighlightRef.current = false;
          onHydratedComplete?.(editor);
        });
      });
    } catch (err) {
      isHydratingRef.current = false;
      canvasDocumentHydrating.active = false;
      skipNewlyAddedHighlightRef.current = false;
      clearNewlyAddedShapes(editor.getContainer());
      throw err;
    }
  }, [decodedPersonName, decodedHashtag]);

  const syncEditorToBoard = useCallback(
    (editor, acts, brd, key, onHydrated) => {
      if (!editor || !brd) return;
      if (hydratedBoardKeyRef.current === key) {
        onHydrated?.();
        return;
      }

      hydratedBoardKeyRef.current = key;
      syncedActivityIdsRef.current = acts.map(a => getActivityId(a)).join(',');

      const viewport = resolveBoardViewport(brd, decodedPersonName, decodedHashtag, {
        preferLocal: dataRef.current.canEdit,
      });
      const hasContent = brd.placements?.length || hasPersistedCanvasContent(brd);
      if (!hasContent && !viewport) {
        onHydrated?.();
        return;
      }

      hydrateEditor(editor, acts, brd, hydrated => {
        onHydrated?.();
        if (normalizeExistingBoardLinks(hydrated)) {
          annotationMigrationPersistRef.current?.();
        }
      });
    },
    [hydrateEditor, decodedPersonName, decodedHashtag]
  );

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !isBoardDataReady) return;

    const { activities: acts, board: brd } = dataRef.current;
    syncEditorToBoard(editor, acts, brd, boardKey, scheduleCanvasContextAnalysis);
  }, [isBoardDataReady, boardKey, syncEditorToBoard, scheduleCanvasContextAnalysis]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !isBoardDataReady || hydratedBoardKeyRef.current !== boardKey) return;

    const brd = dataRef.current.board;
    const syncKey = buildPinSyncKey(activities, brd);
    if (syncedActivityIdsRef.current === syncKey) return;

    syncedActivityIdsRef.current = syncKey;
    isHydratingRef.current = true;
    canvasDocumentHydrating.active = true;
    try {
      editor.run(() => syncPinShapes(editor, activities, brd));
    } finally {
      isHydratingRef.current = false;
      canvasDocumentHydrating.active = false;
      scheduleCanvasContextAnalysis();

      const pendingPinId = pendingPinActivityIdRef.current;
      if (pendingPinId) {
        pendingPinActivityIdRef.current = null;
        analyzePinAdded?.({
          activityId: pendingPinId,
          placePending: true,
        });
      }
    }
  }, [
    activities,
    board,
    boardKey,
    isBoardDataReady,
    scheduleCanvasContextAnalysis,
    analyzePinAdded,
  ]);

  const handleMount = useCallback(
    editor => {
      editorRef.current = editor;
      applyEditorReadonly(editor, dataRef.current.canEdit);
      editor.setCameraOptions({ zoomSteps: CANVAS_CAMERA_ZOOM_STEPS, zoomSpeed: 2.5 });
      syncBoardCameraCssVars(editor.getContainer(), editor.getCamera().z);

      const { activities: acts, board: brd } = dataRef.current;
      syncEditorToBoard(editor, acts, brd, boardKey, scheduleCanvasContextAnalysis);

      const unregisterPinCardDragGuard = registerPinCardDragGuard(editor);
      const unregisterEraserCanvasDelete = registerEraserCanvasDelete(editor, {
        canDelete: () => dataRef.current.canEdit && !isHydratingRef.current,
      });
      const unregisterUserPinDeleteCascade = registerUserPinDeleteCascade(editor, {
        activityStillExists: activityId => {
          const id = String(activityId);
          return useBoardStore
            .getState()
            .activities.some(a => getActivityId(a) === id);
        },
        onDeleteUserPins: activityIds => {
          const store = useBoardStore.getState();
          return deleteUserPins(editor, activityIds, {
            deleteActivities: store.deleteActivities,
            activities: store.activities,
            board: store.board,
            updatePlacementsLocal: store.updatePlacementsLocal,
            saveSnapshot: createOpsOnlyDocumentSaver(editor),
            confirm: true,
            onDeleted: (deletedIds) => {
              enqueueDigestDeleteRef.current?.(deletedIds);
            },
          });
        },
      });
      const unregisterFramePinFollow = registerFramePinFollow(editor);
      const unregisterFrameArrowTargeting = registerFrameArrowTargeting(editor);
      const unregisterBoardLinkDefaults = registerBoardLinkDefaults(editor);
      const unregisterArrowSelectConnected = registerArrowSelectConnected(editor);
      const unregisterCanvasShapeContextMenu = registerCanvasShapeContextMenu(editor);
      const unregisterNewlyAddedHighlight = registerNewlyAddedShapeHighlight(editor, {
        shouldSkip: () => skipNewlyAddedHighlightRef.current,
      });

      const unregisterActorMeta = editor.sideEffects.registerAfterCreateHandler('shape', shape => {
        if (!dataRef.current.canEdit) return;
        const currentUser = userActorRef.current;
        if (!currentUser?.id) return;
        if (shape.meta?.createdBy) return;
        editor.updateShape({
          id: shape.id,
          type: shape.type,
          meta: {
            ...shape.meta,
            createdBy: currentUser.id,
            createdByUsername: currentUser.username || '',
            createdByProfileImageUrl: currentUser.profileImageUrl || '',
            updatedBy: currentUser.id,
            updatedByUsername: currentUser.username || '',
            updatedByProfileImageUrl: currentUser.profileImageUrl || '',
          },
        });
      });
      const unregisterActorUpdate = editor.sideEffects.registerAfterChangeHandler('shape', (prev, next) => {
        if (!dataRef.current.canEdit) return;
        const currentUser = userActorRef.current;
        if (!currentUser?.id) return;
        if (prev.x === next.x && prev.y === next.y && prev.rotation === next.rotation) return;
        if (String(next.meta?.updatedBy) === String(currentUser.id)) return;
        editor.updateShape({
          id: next.id,
          type: next.type,
          meta: {
            ...next.meta,
            updatedBy: currentUser.id,
            updatedByUsername: currentUser.username || '',
            updatedByProfileImageUrl: currentUser.profileImageUrl || '',
          },
        });
      });

      // Match right-click paste (image / URL / text) for external content.
      // Leave tldraw native shape copy/paste alone.
      const handlePasteClipboard = async event => {
        if (!dataRef.current.canEdit || isHydratingRef.current) return;

        const target = event.target;
        const tag = String(target?.tagName || '').toLowerCase();
        const editable =
          target?.isContentEditable
          || tag === 'input'
          || tag === 'textarea'
          || tag === 'select';
        if (editable || editor.getEditingShapeId?.()) return;
        if (isTldrawClipboardEvent(event)) return;

        const pastePayload = payloadFromPasteEvent(event);
        const kind = pastePayload ? classifyClipboardPayload(pastePayload) : null;
        if (!kind) return;

        event.preventDefault();
        event.stopPropagation();

        try {
          await promptAndPasteClipboard(editor, resolvePastePagePoint(editor), {
            hashtag: decodedHashtag,
            saveSnapshot: createOpsOnlyDocumentSaver(editor),
            analyzePinAdded: (...args) => analyzePinAddedRef.current?.(...args),
            payload: pastePayload,
          });
        } catch (err) {
          console.warn('[canvas-paste] failed', err?.message || err);
        }
      };
      editor.getContainer()?.addEventListener('paste', handlePasteClipboard, true);

      let viewportTimer;
      let placementTimer;
      let frameTimer;
      let opsTimer;
      let pendingDocumentOps = [];
      // Once a non-label edit joins the pending ops queue, keep the short debounce
      // so moves/deletes are not delayed behind label typing.
      let pendingOpsHadNonLabel = false;
      let lastInstanceChangeAt = 0;

      const persistPlacementsNow = () => {
        if (!dataRef.current.canEdit) return;
        // Pin drops must save even if a short hydrate/sync flag is set — otherwise
        // moves never reach the server and vanish on refresh.
        clearTimeout(placementTimer);
        const placements = shapesToPlacements(
          editor,
          dataRef.current.board?.placements,
          dataRef.current.activities
        );
        if (!shouldPersistPlacements(placements, dataRef.current.board?.placements)) {
          return;
        }
        // savePlacements updates local then PATCHes. Do not call updatePlacementsLocal
        // first — that makes savePlacements see no change and skip the API, so
        // frameId / position membership never persists.
        savePlacements(placements);
      };

      registerPlacementPersister(persistPlacementsNow);

      const persistViewportNow = () => {
        if (!dataRef.current.canEdit || isHydratingRef.current) return;
        clearTimeout(viewportTimer);
        const viewport = cameraToViewport(editor);
        writeLocalBoardViewport(decodedPersonName, decodedHashtag, viewport);
        saveViewport(viewport);
      };

      const persistOpsNow = () => {
        if (!dataRef.current.canEdit || isHydratingRef.current) return;
        const ops = pendingDocumentOps;
        pendingDocumentOps = [];
        pendingOpsHadNonLabel = false;
        if (!ops.length) return;
        try {
          const snapshot = extractBoardDocumentSnapshot(editor);
          if (snapshot) updateSnapshotLocal(snapshot);
        } catch {
          /* ignore */
        }
        saveDocumentOps(ops);
      };

      const persistFramesNow = () => {
        if (!dataRef.current.canEdit || isHydratingRef.current) return;
        clearTimeout(frameTimer);
        saveFrames(framesFromEditor(editor));
      };

      annotationMigrationPersistRef.current = null;

      const unregisterBoardAnnotationPersist = registerBoardAnnotationPersist(editor, {
        shouldPersist: () => dataRef.current.canEdit && !isHydratingRef.current,
        onPersist: (snapshot, { frames } = {}) => {
          // Object-update path: keep local snapshot for unload safety, persist
          // board.frames meta. Shape records go through /ops (document listener).
          updateSnapshotLocal(snapshot);
          saveFrames(Array.isArray(frames) ? frames : []);
        },
      });

      const flushPendingBoardEdits = () => {
        clearTimeout(opsTimer);
        clearTimeout(frameTimer);
        clearTimeout(placementTimer);
        persistOpsNow();
        persistFramesNow();
        persistPlacementsNow();
        persistViewportNow();
        // Do not PATCH full snapshot here — it races object ops and caused 409 wipes.
      };

      const handlePageHide = () => {
        if (!dataRef.current.canEdit || isHydratingRef.current) return;
        clearTimeout(placementTimer);
        clearTimeout(viewportTimer);
        clearTimeout(frameTimer);
        clearTimeout(opsTimer);
        // Flush object-level pending writes first.
        persistOpsNow();
        persistFramesNow();
        const existingPlacements = dataRef.current.board?.placements;
        const placements = shapesToPlacements(
          editor,
          existingPlacements,
          dataRef.current.activities
        );
        const viewport = cameraToViewport(editor);
        writeLocalBoardViewport(decodedPersonName, decodedHashtag, viewport);
        patchBoardViewportKeepalive(decodedHashtag, viewport);
        if (shouldPersistPlacements(placements, existingPlacements)) {
          updatePlacementsLocal(placements);
          const placementOps = buildPlacementOps(existingPlacements, placements);
          if (placementOps.length) {
            patchBoardPlacementOpsKeepalive(decodedHashtag, placementOps, {
              baseRevision: dataRef.current.board?.canvasRevision,
            });
          }
        }
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState !== 'hidden') return;
        // Regular fetch while the document is still alive — more reliable than
        // keepalive for large tldraw snapshots (Chrome ~64KB keepalive limit).
        flushPendingBoardEdits();
      };

      window.addEventListener('pagehide', handlePageHide);
      window.addEventListener('beforeunload', handlePageHide);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Camera lives in session-scoped records (not document).
      // Keep CSS vars in sync every frame; debounce persistence so zoom animation stays smooth.
      const unlistenCamera = editor.store.listen(
        () => {
          lastInstanceChangeAt = Date.now();
          const viewport = cameraToViewport(editor);
          syncBoardCameraCssVars(editor.getContainer(), viewport.zoom);

          if (!dataRef.current.canEdit || isHydratingRef.current) return;

          clearTimeout(viewportTimer);
          viewportTimer = setTimeout(() => {
            if (!dataRef.current.canEdit || isHydratingRef.current) return;
            const next = cameraToViewport(editor);
            writeLocalBoardViewport(decodedPersonName, decodedHashtag, next);
            saveViewport(next);
          }, VIEWPORT_SAVE_DEBOUNCE_MS);
        },
        { scope: 'session', source: 'user' }
      );

      const unlistenDocument = editor.store.listen(
        entry => {
          if (!dataRef.current.canEdit || isHydratingRef.current) return;
          if (isCanvasStrokeInProgress(editor)) return;

          const { changes } = entry;

          if (isPinCardIndexOnlyUpdate(changes)) return;

          if (isPinCardDocumentChange(changes) || isFramePositionChange(changes)) {
            const duringCameraChange = Date.now() - lastInstanceChangeAt < 1000;
            if (
              isPinCardDocumentChange(changes) &&
              duringCameraChange &&
              isPinCardHeightOnlyUpdate(changes)
            ) {
              return;
            }

            clearTimeout(placementTimer);
            placementTimer = setTimeout(persistPlacementsNow, 600);
            if (isPinCardDocumentChange(changes) && !isFramePositionChange(changes)) {
              return;
            }
          }

          const labelOnly = isLabelOnlyDocumentChange(changes);

          if (hasFrameDocumentChange(changes)) {
            clearTimeout(frameTimer);
            // Frame rename while typing uses the longer label debounce.
            frameTimer = setTimeout(
              persistFramesNow,
              labelOnly ? LABEL_PERSIST_MS : FRAME_SAVE_DEBOUNCE_MS
            );
          }

          const ops = buildBoardDocumentOps(changes);
          if (ops.length) {
            pendingDocumentOps = pendingDocumentOps.concat(ops);
            if (!labelOnly) pendingOpsHadNonLabel = true;
            clearTimeout(opsTimer);
            opsTimer = setTimeout(
              persistOpsNow,
              !pendingOpsHadNonLabel && labelOnly
                ? LABEL_PERSIST_MS
                : OPS_SAVE_DEBOUNCE_MS
            );
          }

        },
        // Include app-driven pin drags (custom pointer handler), not only stock
        // tldraw tool gestures tagged as source "user".
        { scope: 'document' }
      );

      editorRef.current._boardCleanup = () => {
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('beforeunload', handlePageHide);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        // Flush pending edits before cancelling timers — otherwise frame/text/
        // arrow/line moves within the debounce window are dropped on navigate.
        flushPendingBoardEdits();
        unlistenCamera();
        unlistenDocument();
        unregisterPinCardDragGuard();
        unregisterEraserCanvasDelete();
        unregisterUserPinDeleteCascade();
        unregisterFramePinFollow();
        unregisterFrameArrowTargeting();
        unregisterBoardLinkDefaults();
        unregisterArrowSelectConnected();
        unregisterCanvasShapeContextMenu();
        unregisterBoardAnnotationPersist();
        unregisterNewlyAddedHighlight();
        unregisterActorMeta();
        unregisterActorUpdate();
        editor.getContainer()?.removeEventListener('paste', handlePasteClipboard, true);
        clearNewlyAddedShapes(editor.getContainer());
        unregisterPlacementPersister();
        clearTimeout(viewportTimer);
        clearTimeout(placementTimer);
        clearTimeout(frameTimer);
        clearTimeout(opsTimer);
        canvasDocumentHydrating.active = false;
      };
    },
    [
      boardKey,
      decodedPersonName,
      decodedHashtag,
      syncEditorToBoard,
      scheduleCanvasContextAnalysis,
      updatePlacementsLocal,
      updateSnapshotLocal,
      savePlacements,
      saveViewport,
      saveDocumentOps,
      saveFrames,
    ]
  );

  useEffect(
    () => () => {
      editorRef.current?._boardCleanup?.();
    },
    []
  );

  if (!isBoardDataReady) {
    return (
      <Box className="board-canvas-loading">
        <LoadingScreen fullScreen={false} compact message="Opening AI memory board" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="board-canvas-loading">
        <Typography color="error">
          {errorCode === 'BOARD_PRIVATE'
            ? 'This board is private. Ask the owner for a share link.'
            : error}
        </Typography>
      </Box>
    );
  }

  return (
    <BoardActivitiesProvider activities={activities} loadedTime={loadedTime}>
      <BoardAiProvider value={boardAiContextValue}>
        <BoardAlertHost />
        <Box className="board-canvas-root" sx={{ position: 'relative' }}>
          {(isOwner || role === 'editor') ? (
            <BoardSharePanel
              personName={decodedPersonName}
              hashtag={decodedHashtag}
              isOwner={isOwner || role === 'owner'}
              role={role}
            />
          ) : null}
          {canEdit ? (
            <BoardAiToolbar
              editorRef={editorRef}
              hashtag={decodedHashtag}
              activities={activities}
              board={board}
              updatePlacementsLocal={updatePlacementsLocal}
              savePlacements={savePlacements}
              saveFrames={saveFrames}
              saveSnapshot={persistDocumentOpsOnly}
              deleteActivities={deleteActivities}
              contextActions={contextActions}
              contextLoading={contextLoading}
              contextError={contextError}
              thinkingPhase={thinkingPhase}
              lastJobMetrics={lastJobMetrics}
              refreshContextActions={refreshContextActions}
              dismissNextAction={dismissNextAction}
              recordGapInterest={recordGapInterest}
              recordUserDecision={recordUserDecision}
            />
          ) : (
            <Box className="board-canvas-toolbar">
              <Typography variant="caption" color="text.secondary">
                View only — you can open pins to see their original targets
              </Typography>
            </Box>
          )}
          <Box className="board-canvas-stage">
            <Tldraw
              key={`${boardKey}:${canEdit ? 'edit' : 'view'}`}
              shapeUtils={BOARD_SHAPE_UTILS}
              components={tldrawComponents}
              overrides={tldrawOverrides}
              onMount={handleMount}
            />
            <BoardSettledPanel
              editorRef={editorRef}
              activities={activities}
              board={board}
              contextActions={contextActions}
            />
            {canEdit ? (
              <BoardEmptyOnboarding
                editorRef={editorRef}
                canEdit={canEdit}
                pinCount={pinCount}
                canvasReady={canvasHydrationTick > 0}
              />
            ) : null}
            {canEdit ? (
              <BoardChatLauncher
                hashtag={decodedHashtag}
                editorRef={editorRef}
                board={board}
                activities={activities}
                updatePlacementsLocal={updatePlacementsLocal}
                savePlacements={savePlacements}
                saveFrames={saveFrames}
                saveSnapshot={persistDocumentOpsOnly}
                deleteActivities={deleteActivities}
                analyzePinAdded={analyzePinAdded}
              />
            ) : (
              <PinnedChatViewer
                open={pinnedViewer.open}
                conversationId={pinnedViewer.conversationId}
                onClose={() => setPinnedViewer({ open: false, conversationId: null })}
              />
            )}
            {!canEdit ? (
              <BoardCurrentStep
                title={readCurrentStep(board)}
                steps={board?.steps || []}
                currentStepId={board?.currentStepId || ''}
                achievementRate={contextActions?.achievementRate}
                variant="overlay"
              />
            ) : null}
            <PinFocusPanel
              activity={activities.find(a => getActivityId(a) === selectedActivityId) || null}
              open={Boolean(selectedActivityId)}
              onClose={() => setSelectedActivityId(null)}
              loadedTime={loadedTime}
            />
            <YoutubeVideoModal
              video={youtubePlayback}
              extensionId={extensionId}
              onClose={closeYoutubePlayback}
              onMoment={(seconds, imageDataUrl) => {
                if (youtubePlayback?.activityId) {
                  saveYoutubeMoment(youtubePlayback.activityId, seconds, imageDataUrl);
                }
              }}
            />
          </Box>
        </Box>
      </BoardAiProvider>
    </BoardActivitiesProvider>
  );
};

export default TldrawBoard;
